import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
    Star, 
    Quote, 
    ChevronLeft, 
    ChevronRight,
    MessageSquareQuote
} from 'lucide-react';

const testimonials = [
    {
        id: 1,
        name: 'Priya & Arjun Sharma',
        event: 'Wedding Reception',
        location: 'Mumbai',
        rating: 5,
        content: "Hawayein made our wedding reception absolutely magical! Yash's voice gave us goosebumps, and the entire band kept our guests dancing till midnight. They played everything from romantic Bollywood classics to peppy party numbers. Couldn't have asked for a better experience!",
        avatar: 'PS',
    },
    {
        id: 2,
        name: 'Rahul Kapoor',
        event: 'Corporate Annual Day',
        location: 'Bangalore',
        rating: 5,
        content: "We booked Hawayein for our company's annual day celebration, and they exceeded all expectations. Professional, punctual, and incredibly talented. The energy they brought to the stage got even our most reserved employees on the dance floor!",
        avatar: 'RK',
    },
    {
        id: 3,
        name: 'Meera Iyer',
        event: '50th Birthday Celebration',
        location: 'Chennai',
        rating: 5,
        content: "Organized a surprise party for my father's 50th birthday and Hawayein was the highlight. They learned his favorite old Hindi songs and the look on his face was priceless. Thank you for making this milestone so special for our family!",
        avatar: 'MI',
    },
    {
        id: 4,
        name: 'Vikram & Neha Singh',
        event: 'Sangeet Night',
        location: 'Delhi',
        rating: 5,
        content: "The sangeet night wouldn't have been the same without Hawayein! They coordinated perfectly with our choreographers and made every performance segment seamless. Yash even joined for a fun segment with the bride and groom. Amazing memories!",
        avatar: 'VS',
    },
    {
        id: 5,
        name: 'Ananya Desai',
        event: 'College Fest',
        location: 'Pune',
        rating: 5,
        content: "Our college fest was a massive hit thanks to Hawayein's electrifying performance. The crowd of 3000+ students was completely mesmerized. The band's stage presence and interaction with the audience was phenomenal. Definitely booking them again next year!",
        avatar: 'AD',
    },
];

export const TestimonialsSection = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(0);

    const navigate = (dir) => {
        setDirection(dir);
        setCurrentIndex((prev) => 
            dir === 1 
                ? (prev + 1) % testimonials.length 
                : (prev - 1 + testimonials.length) % testimonials.length
        );
    };

    // Auto-advance
    useEffect(() => {
        const timer = setInterval(() => {
            setDirection(1);
            setCurrentIndex((prev) => (prev + 1) % testimonials.length);
        }, 6000);
        return () => clearInterval(timer);
    }, []);

    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? 300 : -300,
            opacity: 0,
        }),
        center: {
            x: 0,
            opacity: 1,
        },
        exit: (direction) => ({
            x: direction < 0 ? 300 : -300,
            opacity: 0,
        }),
    };

    return (
        <section className="py-20 lg:py-28 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-14 lg:mb-16"
                >
                    <div className="inline-flex items-center gap-2 mb-4">
                        <MessageSquareQuote className="w-5 h-5 text-primary" />
                        <span className="text-sm font-body font-medium text-primary uppercase tracking-wider">
                            Testimonials
                        </span>
                    </div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
                        What Our Clients Say
                    </h2>
                    <p className="text-muted-foreground text-base sm:text-lg font-body max-w-2xl mx-auto">
                        Real stories from real clients who trusted us with their special moments
                    </p>
                </motion.div>

                {/* Testimonials Slider */}
                <div className="max-w-4xl mx-auto">
                    <div className="relative">
                        {/* Navigation Buttons */}
                        <button
                            onClick={() => navigate(-1)}
                            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-16 z-10 w-12 h-12 rounded-full glass border border-border/50 flex items-center justify-center text-foreground hover:border-primary/50 hover:text-primary transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => navigate(1)}
                            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-16 z-10 w-12 h-12 rounded-full glass border border-border/50 flex items-center justify-center text-foreground hover:border-primary/50 hover:text-primary transition-colors"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>

                        {/* Testimonial Card */}
                        <div className="overflow-hidden px-4">
                            <AnimatePresence mode="wait" custom={direction}>
                                <motion.div
                                    key={currentIndex}
                                    custom={direction}
                                    variants={variants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                                >
                                    <Card className="card-premium rounded-3xl">
                                        <CardContent className="p-8 lg:p-12">
                                            {/* Quote Icon */}
                                            <div className="mb-6">
                                                <Quote className="w-12 h-12 text-primary/30" />
                                            </div>

                                            {/* Stars */}
                                            <div className="flex gap-1 mb-6">
                                                {[...Array(5)].map((_, i) => (
                                                    <Star
                                                        key={i}
                                                        className={`w-5 h-5 ${
                                                            i < testimonials[currentIndex].rating
                                                                ? 'fill-amber text-amber'
                                                                : 'text-muted'
                                                        }`}
                                                    />
                                                ))}
                                            </div>

                                            {/* Content */}
                                            <p className="text-foreground text-lg lg:text-xl font-body leading-relaxed mb-8">
                                                "{testimonials[currentIndex].content}"
                                            </p>

                                            {/* Author */}
                                            <div className="flex items-center gap-4">
                                                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-foreground font-heading font-bold text-lg">
                                                    {testimonials[currentIndex].avatar}
                                                </div>
                                                <div>
                                                    <h4 className="font-heading font-semibold text-foreground">
                                                        {testimonials[currentIndex].name}
                                                    </h4>
                                                    <p className="text-sm text-muted-foreground font-body">
                                                        {testimonials[currentIndex].event} • {testimonials[currentIndex].location}
                                                    </p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Dots Indicator */}
                        <div className="flex justify-center gap-2 mt-8">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        setDirection(index > currentIndex ? 1 : -1);
                                        setCurrentIndex(index);
                                    }}
                                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                                        index === currentIndex
                                            ? 'bg-primary w-8'
                                            : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
